import { useEffect, useMemo, useState } from "react";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, LayoutGrid, List } from "lucide-react";
import { useVendas, useResumoVendas, useCompradores, useExcluirVenda, useRecalcularRateio, useVariedadesAtivas } from "@/hooks/use-vendas";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import { useToast } from "@/hooks/use-toast";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import type { VendaWithRelations } from "@shared/schema";
import { recortarItensPorVariedade, resumirClassificacao, percentualDaClasse, cargaEmPesagem } from "@shared/vendas-itens";
import { dinheiroEmAberto, estadoDinheiroDaVenda, type CompartilhamentoResumo } from "@shared/mercado";
import { useCompartilhamentos } from "@/hooks/use-mercado";
import { FormalizacaoChip } from "@/components/mercado/formalizacao-chip";
import { ChipDinheiro, ChipDivergencia } from "@/components/mercado/dinheiro-chip";
import { BotaoFormalizar } from "@/components/mercado/botao-formalizar";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { StatCard, StatCardGrid } from "@/components/data/stat-card";
import { StatusBadge } from "@/components/data/status-badge";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableFooter,
  DataTableRow, DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import { Segmented } from "@/components/layout/segmented";
const MODO_VISAO_STORAGE_KEY = "rizoma:vendas:modoVisao";

// ── O estado da lista mora na URL (CLAUDE.md § Navegação e estado de lista) ───
// Default: mês atual + EMITIDA/PAGA (preset "em aberto e pagas") — rascunho ainda não é
// venda de verdade e cancelada não deveria contar no dia a dia (rev. 2 da spec Colhido).
// `de`/`ate` nascem VAZIOS e são derivados na hora (primeiro dia do mês / hoje): guardar a
// data calculada como estado a punha na URL sem ninguém ter escolhido nada, e
// `?periodo=personalizado` sozinho precisa continuar abrindo com a janela padrão.
// ⚠️ Constantes de MÓDULO: são deps de useMemo dentro do hook.
const PADRAO_VENDAS = {
  periodo: "mes-atual",
  de: "",
  ate: "",
  variedade: "todas",
  comprador: "todos",
  status: "EM_ABERTO_PAGAS",
  q: "",
};
const PERMITIDOS_VENDAS = {
  periodo: ["mes-atual", "mes-passado", "ultimos-30-dias", "personalizado", "tudo"],
  // COM_DIVERGENCIA é um preset do SERVIDOR (como EM_ABERTO_PAGAS): os cards do
  // topo descrevem o mesmo recorte da lista, por construção.
  status: ["todos", "EM_ABERTO_PAGAS", "RASCUNHO", "EMITIDA", "PAGA", "CANCELADA", "COM_DIVERGENCIA"],
} as const;

function fmtMoney(v: number | string) {
  return `R$ ${parseFloat(String(v)).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtKg(v: number | string) {
  return `${parseFloat(String(v)).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`;
}

// `primeiroDiaDoMes` fica: monta Date.UTC(ano, mês, 1) a partir do ano/mês LOCAIS, e o dia 1
// não escorrega. Quem escorregava era o `todayStr` local, trocado por `hojeNaFazenda()`.
function primeiroDiaDoMes(): string {
  const hoje = new Date();
  return new Date(Date.UTC(hoje.getFullYear(), hoje.getMonth(), 1)).toISOString().slice(0, 10);
}

function fmtDataCurta(data: string) {
  return new Date(data + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

interface ResumoClassificacaoUI {
  porClasse: Array<{ classificacao: string; kg: number; valor: number }>;
  kgClassificado: number;
  kgSemClassificacao: number;
  variedadesComClassificacao: string[];
  variedadesSemClassificacao: string[];
}

// A barra de classificação é um GRÁFICO: a cor aqui é o dado (1ª, 2ª, 3ª numa
// escala de qualidade), não estado. Por isso hex + `style`, como a cor de fita —
// os quatro tons semânticos diriam "3ª = erro", que não é o que 3ª significa.
const COR_DA_CLASSE: Record<string, string> = { "1ª": "#15803d", "2ª": "#84cc16", "3ª": "#fcd34d" };
const COR_SEM_CLASSE = "hsl(220 9% 40%)";

/**
 * No celular cada filtro ocupa a linha inteira — é o `w-full` que já vem do
 * `SelectTrigger`, e por isso aqui não há largura nenhuma antes de `md`. As
 * larguras fixas antigas (`w-36`/`w-40`) valiam nos dois mundos e erravam nos
 * dois: no celular espremiam quatro controles numa faixa que rolava escondida,
 * e na tela larga não cabiam o próprio rótulo. Em `md` cada gatilho recebe a
 * largura que cabe o SEU rótulo mais longo — "Todos compradores" mede 179px e
 * morava em `w-40` (160px), daí o "Todos..." do desktop.
 *
 * As duas datas do período personalizado são a exceção: elas dividem uma linha
 * com o "até" no meio, porque separá-las em duas linhas quebra a leitura do
 * intervalo. `text-xs` não é enfeite — no iPhone o `input[type=date]` escreve
 * "11 de ago. de 2026" por extenso, e em 16px esse texto vaza a borda.
 */
const TRIGGER_FILTRO = "shrink-0";
const DATA_FILTRO = "flex-[1_1_8rem] min-w-0 text-xs md:flex-none md:w-36";

/**
 * Classificação é o que o Hugo CONTROLA — é operação de packing e ponto de colheita, não
 * mercado. Em agosto: 82,7% de 1ª, e a 2ª vale 61% da 1ª (R$ 1,84 × R$ 1,13/kg), então cada
 * 1% que sai da 1ª custa ~R$ 305 no mês.
 *
 * (Descartada a ideia anterior de "R$/kg por comprador": o preço do dia é igual para todos, e
 * a diferença observada era composição de variedade e data. Não reintroduzir.)
 */
function CardClassificacao({ classificacao, kgTotalRecorte }: {
  classificacao?: ResumoClassificacaoUI;
  kgTotalRecorte: number;
}) {
  if (!classificacao) {
    return <StatCard rotulo="Classificação" valor="—" />;
  }

  const { porClasse, kgClassificado, kgSemClassificacao, variedadesComClassificacao, variedadesSemClassificacao } = classificacao;

  // Nada classificado no recorte (ex.: filtro Nanica): o card não morre num "—". Vira
  // "Composição", que é a pergunta que ainda faz sentido ali.
  if (kgClassificado <= 0) {
    const pctSemClasse = kgTotalRecorte > 0 ? (kgSemClassificacao / kgTotalRecorte) * 100 : 100;
    return (
      <StatCard
        rotulo="Composição"
        testId="card-classificacao"
        valor={`${pctSemClasse.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}%`}
        unidade={variedadesSemClassificacao.join(", ") || "—"}
        legenda={
          <>
            <div className="flex h-2 rounded-full overflow-hidden bg-muted">
              <div className="bg-muted-foreground" style={{ width: "100%" }} />
            </div>
            <p className="mt-1 text-[10px]" data-testid="texto-composicao">
              nenhuma variedade do recorte tem classificação
            </p>
          </>
        }
      />
    );
  }

  // ⚠️ SEMPRE sobre kgClassificado. Sobre o kg total, uma carga com Nanica mostraria "% de 1ª"
  // falsamente baixo — a venda 034 daria 39,4% em vez de 86,4%.
  const pct = (classe: string) =>
    ((porClasse.find(c => c.classificacao === classe)?.kg ?? 0) / kgClassificado) * 100;
  const primeira = pct("1ª");

  return (
    <StatCard
      rotulo={`Classificação${variedadesComClassificacao.length > 0 ? ` · ${variedadesComClassificacao.join(", ")}` : ""}`}
      testId="card-classificacao"
      valor={`${primeira.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
      unidade="de 1ª"
      legenda={
        <>
          <div className="flex h-2 rounded-full overflow-hidden bg-muted">
            {porClasse.map(c => (
              <div key={c.classificacao} style={{ width: `${pct(c.classificacao)}%`, backgroundColor: COR_DA_CLASSE[c.classificacao] ?? COR_SEM_CLASSE }} />
            ))}
          </div>
          <p className="mt-1 text-[10px] leading-snug" data-testid="texto-pct-primeira">
            {porClasse.map((c, i) => (
              <span key={c.classificacao}>
                {i > 0 && " · "}
                {c.classificacao} {pct(c.classificacao).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
              </span>
            ))}
          </p>
          {/* OBRIGATÓRIA quando há kg fora da base: todo número que exclui parte do universo
              declara quanto excluiu, no mesmo lugar onde aparece. */}
          <p className="mt-0.5 text-[10px]" data-testid="texto-base-classificacao">
            base: {fmtKg(kgClassificado)}
            {kgSemClassificacao > 0 && ` · ${fmtKg(kgSemClassificacao)} de ${variedadesSemClassificacao.join(", ")} sem classificação`}
          </p>
        </>
      }
    />
  );
}

function resumoItens(venda: VendaWithRelations): string {
  // Em pesagem a caixa não existe: o item vira "2.502 kg Prata 1ª" em vez de "0 cx Prata 1ª",
  // que afirmaria uma contagem. Achado da varredura de leitores de `quantidadeCaixas`.
  // Milhar e espaço antes da unidade nos dois modos — "9152cx" e "4.891kg" eram dois jeitos
  // de escrever número na mesma linha (print do Hugo, 24/09).
  return venda.itens
    .map(it => {
      const nome = it.produtoVenda.nome.replace(/^Banana /, "");
      return it.modoPeso === "PESAGEM"
        ? `${parseFloat(String(it.pesoTotalKg)).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg ${nome}`
        : `${it.quantidadeCaixas.toLocaleString("pt-BR")} cx ${nome}`;
    })
    .join(" · ");
}

function setoresDaVenda(venda: VendaWithRelations): string[] {
  const nomes = new Set((venda.colheitaAssociacoes ?? []).map(a => a.setor.nome));
  return Array.from(nomes).sort();
}

// venda.pesoMedioPorVariedade já vem pronto do servidor (GET /api/vendas — mesma cascata
// associação→range legado do endpoint /rateio, ver spec FixFallbackRateio). Não recalcular
// no client: foi exatamente duas implementações divergentes do mesmo cálculo que causou o
// bug da venda 023/2026 antes do PR #77 — aqui é só leitura do campo.
function pesoMedioDaVenda(venda: VendaWithRelations) {
  return venda.pesoMedioPorVariedade ?? [];
}

/** Soma do RECORTE da venda. Usa `recortarItensPorVariedade` de shared/ — a MESMA função que o
 *  servidor usa nos cards. Duas implementações do mesmo recorte é o que produziu o bug da
 *  venda 023/2026 (ver comentário acima); não repetir. */
function somaItens(venda: VendaWithRelations, variedadeId?: string) {
  const todos = venda.itens ?? [];
  const doRecorte = recortarItensPorVariedade(todos, variedadeId);
  const soma = doRecorte.reduce(
    (acc, it) => ({
      caixas: acc.caixas + it.quantidadeCaixas,
      kg: acc.kg + parseFloat(String(it.pesoTotalKg)),
      // Valor por variedade vem de `venda_itens.valorTotal`, que é exato: cada linha do
      // romaneio tem o seu R$/kg. Nunca ratear por preço médio.
      valor: acc.valor + parseFloat(String(it.valorTotal)),
    }),
    { caixas: 0, kg: 0, valor: 0 },
  );
  return { ...soma, parcial: doRecorte.length < todos.length };
}

/** `86,4% 1ª` — sempre sobre o kg CLASSIFICADO da linha, nunca sobre o kg total dela. */
function CelulaClassificacao({ venda, variedadeId }: { venda: VendaWithRelations; variedadeId?: string }) {
  const itens = recortarItensPorVariedade(venda.itens ?? [], variedadeId);
  const resumo = resumirClassificacao(itens.map(it => ({
    variedadeId: it.produtoVenda.variedadeId,
    variedadeNome: null, // o nome vem do agregado do topo; aqui só interessa com/sem classe
    classificacao: it.produtoVenda.classificacao ?? null,
    kg: parseFloat(String(it.pesoTotalKg)),
    valor: parseFloat(String(it.valorTotal)),
  })));

  // Carga 100% sem classificação: "não classificada", NUNCA "—". Esse traço já significa
  // "não sei" no app (venda sem colheita associada); aqui o caso é "não se aplica".
  if (resumo.kgClassificado <= 0) {
    const nomes = Array.from(new Set(
      itens.map(it => venda.pesoMedioPorVariedade?.find(v => v.variedadeId === it.produtoVenda.variedadeId)?.variedadeNome)
        .filter((n): n is string => !!n),
    ));
    return (
      <span className="text-muted-foreground text-[11px]" data-testid="texto-nao-classificada">
        {nomes.length > 0 ? `${nomes.join(", ")} · ` : ""}não classificada
      </span>
    );
  }

  const pctPrimeira = percentualDaClasse(resumo, "1ª") ?? 0;
  return (
    <span className="text-[11px]">
      <b>{pctPrimeira.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%</b> 1ª
      {/* Carga mista sem filtro de variedade: diz o que ficou fora, senão o percentual
          parece descrever a carga inteira. */}
      {resumo.kgSemClassificacao > 0 && (
        <span className="text-muted-foreground text-[10px]">
          {" "}({fmtKg(resumo.kgSemClassificacao)} sem classificação fora)
        </span>
      )}
    </span>
  );
}

/** vendaId → o compartilhamento que VALE. Revogado só permanece quando não existe
 *  outro: revogar um link e formalizar de novo é o caminho normal, e o chip precisa
 *  falar do link vivo. */
function indexarPorVenda(linhas: CompartilhamentoResumo[] | undefined): Map<string, CompartilhamentoResumo> {
  const mapa = new Map<string, CompartilhamentoResumo>();
  for (const c of linhas ?? []) {
    const atual = mapa.get(c.vendaId);
    if (!atual || (atual.revogadoEm && !c.revogadoEm)) mapa.set(c.vendaId, c);
  }
  return mapa;
}

/** Formalizar só faz sentido para carga que já saiu — rascunho não é compromisso e
 *  cancelada não existe para o corretor. */
function mostraChipFormalizacao(status: string): boolean {
  return status === "EMITIDA" || status === "PAGA";
}

/**
 * Os chips da venda, lado a lado — três EIXOS, três chips (ver StatusBadge):
 * o dinheiro (A receber · Parcial · Contestado · Paga), a formalização
 * (termina em Formalizada) e, só quando a conferência do comprador não bate, a
 * divergência. Os estados vêm de `shared/`; aqui só se pinta.
 */
function ChipsDaVenda({ venda, compartilhamento, abrir }: {
  venda: VendaWithRelations;
  compartilhamento?: CompartilhamentoResumo;
  abrir: (caminho: string) => void;
}) {
  const vale = compartilhamento && !compartilhamento.revogadoEm ? compartilhamento : undefined;
  const dinheiro = estadoDinheiroDaVenda({ statusVenda: venda.status, statusPagamento: vale ? vale.statusPagamento : null });
  return (
    <>
      <ChipDinheiro estado={dinheiro} testIdSufixo={venda.id} />
      {mostraChipFormalizacao(venda.status) && (
        <FormalizacaoChip compartilhamento={compartilhamento} compradorNome={venda.comprador.nome} emAberto={dinheiroEmAberto(dinheiro)} />
      )}
      {vale?.divergencia && (
        <ChipDivergencia
          divergencia={vale.divergencia}
          onAbrir={() => abrir(`/cacho/vendas/${venda.id}?secao=conferencia`)}
          testIdSufixo={venda.id}
        />
      )}
    </>
  );
}

export default function VendasPage() {
  const { abrir } = useNavegacao();
  // Filtros, busca e período vêm da URL: é o que faz o "Voltar" (botão, gesto do
  // celular), o reload e o link do WhatsApp devolverem exatamente esta lista.
  const { estado: filtros, definir, ativos: filtrosAtivos } = useEstadoNaUrl(PADRAO_VENDAS, PERMITIDOS_VENDAS);
  // Só valem no mobile — em md+ os dois blocos aparecem sempre (classes `md:grid`/`md:flex`).
  // Estado de UI do momento, não recorte da lista: fica em useState, fora da URL.
  const [cardsAbertos, setCardsAbertos] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  // Estado do lado do COMPRADOR (Mercado C1) — eixo independente do status da venda.
  // Vem numa query própria, e não embutido em GET /api/vendas, para não mexer no
  // contrato da lista de vendas por causa de um chip.
  const { data: compartilhamentos } = useCompartilhamentos();
  const compartilhamentosPorVenda = useMemo(() => indexarPorVenda(compartilhamentos), [compartilhamentos]);
  // Persistido em localStorage — se o Hugo deixou selecionado "Tabela", uma nova navegação/
  // reload continua em "Tabela" em vez de sempre voltar pro padrão "Cards". A escolha só é
  // GRAVADA quando o usuário clica no toggle (selecionarModoVisao) — o efeito de breakpoint
  // abaixo força "cards" na viewport mobile sem persistir isso por cima da preferência salva.
  const [modoVisao, setModoVisao] = useState<"cards" | "tabela">(() => {
    if (typeof window === "undefined") return "cards";
    return localStorage.getItem(MODO_VISAO_STORAGE_KEY) === "tabela" ? "tabela" : "cards";
  });
  // PR 2: excluir/pagar é `cacho.vendas.financeiro`; o nome `isAdmin` fica só
  // para não espalhar o diff — o VALOR é a capacidade.
  const isAdmin = usePode("cacho.vendas.financeiro");
  const podeOperar = usePode("cacho.vendas.operar");
  const excluir = useExcluirVenda();
  const recalcular = useRecalcularRateio();
  const { toast } = useToast();

  function selecionarModoVisao(v: "cards" | "tabela") {
    setModoVisao(v);
    localStorage.setItem(MODO_VISAO_STORAGE_KEY, v);
  }

  // Tabela só faz sentido em telas md+ — se encolher abaixo do breakpoint com "tabela"
  // selecionada (inclusive vinda do localStorage), volta pra cards (o toggle também some
  // nesse breakpoint). Usa setModoVisao direto (não selecionarModoVisao) pra não sobrescrever
  // a preferência salva só por causa da viewport do momento.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)"); // Tailwind `md`
    function aplicar() {
      if (!mq.matches) setModoVisao("cards");
    }
    aplicar();
    mq.addEventListener("change", aplicar);
    return () => mq.removeEventListener("change", aplicar);
  }, []);

  const periodo = filtros.periodo;
  const status = filtros.status;
  const compradorId = filtros.comprador;
  const variedadeId = filtros.variedade;
  // Busca: filtro NO CLIENTE, sobre a lista que já veio. Não entra em `filtroAtivo`
  // — se entrasse, viraria queryKey e cada tecla seria um round-trip.
  const busca = filtros.q;

  // Vazio na URL = a janela padrão, calculada aqui. Assim `?periodo=personalizado`
  // sozinho abre no mês corrente e a data só vai para a URL quando o Hugo a escolhe.
  const dataInicioPersonalizado = filtros.de || primeiroDiaDoMes();
  const dataFimPersonalizado = filtros.ate || hojeNaFazenda();

  const personalizado = periodo === "personalizado"
    ? { dataInicio: dataInicioPersonalizado, dataFim: dataFimPersonalizado }
    : undefined;

  // UM objeto de filtro, os DOIS hooks. O topo descreve a tabela por construção: não há como
  // passar um filtro pra lista e esquecer do resumo, que era exatamente o bug — três cards
  // ignoravam comprador e situação enquanto o card "Vendas" (vendas.length) os seguia.
  const filtroAtivo = {
    periodo,
    dataInicio: personalizado?.dataInicio,
    dataFim: personalizado?.dataFim,
    compradorId: compradorId === "todos" ? undefined : compradorId,
    status: status === "todos" ? undefined : status,
    variedadeId: variedadeId === "todas" ? undefined : variedadeId,
  };

  const { data: vendas = [], isLoading } = useVendas(filtroAtivo);
  const { data: resumo } = useResumoVendas(filtroAtivo);
  const { data: compradores = [] } = useCompradores();
  const { data: variedades = [] } = useVariedadesAtivas();

  // A busca recorta a lista JÁ carregada; os cards de cima continuam descrevendo o
  // filtro (o recorte do servidor), não o que se está procurando — buscar "Nº 034"
  // não pode fazer o total do período mudar de valor.
  const vendasVisiveis = vendas.filter(v =>
    combina(busca, v.numero ? `${v.numero}` : "", v.numero ? `Nº ${String(v.numero).padStart(3, "0")}/${v.ano}` : "rascunho", v.comprador.nome),
  );

  const variedadeSelecionada = variedadeId === "todas" ? undefined : variedadeId;
  const nomeVariedadeFiltrada = variedades.find(v => v.id === variedadeId)?.nome;
  // Rótulo dos cards quando há recorte de variedade — o card precisa dizer que descreve só
  // uma parte da carga, senão vira o mesmo tipo de número sem base declarada.
  const escopoVariedade = nomeVariedadeFiltrada ? ` · ${nomeVariedadeFiltrada}` : "";
  // Recalcula a JANELA DE COLHEITA que estas vendas cobrem — nunca o filtro de data da tela.
  // O filtro delimita `vendas.data`; o rateio vive em `colheitas.data_colheita`. Passar um no
  // lugar do outro seria o mesmo erro de janela do saldo, entrando por outra porta.
  async function handleRecalcularRateio() {
    // `janelaRecalculo` (associações), NUNCA `janelaSaldo` (linhas de rateio): recalcular pela
    // janela do rateio é ovo e galinha — uma venda que nunca teve rateio não contribui pra
    // aquela janela, então o botão nunca alcançaria justamente o período que precisa dele.
    const janela = resumo?.colhido.janelaRecalculo;
    if (!janela) return; // o botão já está desabilitado neste caso; isto é só o type-guard
    try {
      const r = await recalcular.mutateAsync({ de: janela.inicio, ate: janela.fim });
      toast({
        title: "Rateio recalculado",
        description: `${r.linhas} linha(s) em ${r.vendasAfetadas} venda(s) · ${fmtDataCurta(janela.inicio)}–${fmtDataCurta(janela.fim)}`,
      });
    } catch (err: any) {
      toast({ title: "Erro ao recalcular rateio", description: err.message, variant: "destructive" });
    }
  }

  async function handleExcluirCard(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    if (!confirm("Excluir esta venda definitivamente? Esta ação NÃO pode ser desfeita.")) return;
    try {
      await excluir.mutateAsync(id);
      toast({ title: "Venda excluída." });
    } catch (err: any) {
      toast({ title: "Erro ao excluir", description: err.message, variant: "destructive" });
    }
  }

  return (
    <div className="p-4 md:p-6 space-y-4 pb-24">
      <PageHeader
        titulo="Vendas"
        acoesSecundarias={
          <Segmented
            className="hidden md:inline-flex"
            valor={modoVisao}
            onChange={selecionarModoVisao}
            opcoes={[
              { id: "cards", label: "Cards", icone: LayoutGrid },
              { id: "tabela", label: "Tabela", icone: List },
            ]}
          />
        }
        acao={
          <Button className={ALVO_TOQUE_BOTAO} onClick={() => abrir("/cacho/vendas/novo")}>
            <Plus className="h-4 w-4 mr-1" />Nova venda
          </Button>
        }
      />

      {/* MOBILE: quatro cards + quatro selects empurravam a primeira venda pra fora da tela, e
          no campo o que se quer é a LISTA. O bloco vira uma linha tocável; os filtros, um botão
          com contador. Em md+ nada muda. */}
      <button
        type="button"
        className="md:hidden w-full rounded-xl border border-atencao/30 bg-atencao-suave p-3 flex items-center justify-between gap-3 text-left"
        onClick={() => setCardsAbertos(v => !v)}
        data-testid="button-resumo-mobile"
      >
        <div>
          <p className="text-[9px] uppercase text-atencao font-semibold">A receber{escopoVariedade}</p>
          <p className="text-lg font-extrabold text-atencao">{resumo ? fmtMoney(resumo.aReceber) : "—"}</p>
        </div>
        <div className="text-right text-[10px] text-muted-foreground leading-snug">
          <p>{resumo ? `${resumo.totalVendas} venda${resumo.totalVendas !== 1 ? "s" : ""}` : "—"}</p>
          {resumo && resumo.colhido.porVariedade.length > 0 && (
            <p>
              {resumo.colhido.porVariedade[0].kgPorCacho.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg/cacho
            </p>
          )}
          <p className="text-atencao font-semibold">{cardsAbertos ? "ocultar ▲" : "ver tudo ▼"}</p>
        </div>
      </button>

      <div className="md:hidden flex gap-2">
        <button
          type="button"
          className={`flex-1 rounded-xl border p-2 text-xs font-semibold ${filtrosAbertos || filtrosAtivos > 0 ? "border-sucesso text-sucesso" : "text-muted-foreground"}`}
          onClick={() => setFiltrosAbertos(v => !v)}
          data-testid="button-filtros-mobile"
        >
          Filtros{filtrosAtivos > 0 ? ` · ${filtrosAtivos}` : ""}
        </button>
      </div>

      {/* Os quatro cards eram quatro componentes diferentes — um com borda âmbar, um com
          fundo verde, um sem nada e um com barra dentro. Agora são o mesmo `StatCard`:
          o estado vive na legenda, nunca na moldura. */}
      <StatCardGrid className={`${cardsAbertos ? "grid" : "hidden"} md:grid`}>
        <StatCard
          rotulo={`Vendido${escopoVariedade}`}
          valor={resumo ? fmtMoney(resumo.totalVendido) : "—"}
          legenda={resumo ? `${resumo.totalVendas} venda${resumo.totalVendas !== 1 ? "s" : ""}` : "—"}
        />

        <StatCard
          rotulo={`A receber${escopoVariedade}`}
          valor={resumo ? fmtMoney(resumo.aReceber) : "—"}
          legenda={
            // Quantas, e quantas venceram — não o chip "a receber" embaixo do título
            // "A receber". A contagem sai do servidor (`vendasAReceber`,
            // `vencidasAReceber`), no dia civil da fazenda.
            resumo && resumo.vendasAReceber > 0 ? (
              <span className="flex flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span>{resumo.vendasAReceber} venda{resumo.vendasAReceber !== 1 ? "s" : ""}</span>
                  {resumo.vencidasAReceber > 0 && (
                    <StatusBadge tom="erro" ponto testId="chip-vencidas">
                      {resumo.vencidasAReceber} vencida{resumo.vencidasAReceber !== 1 ? "s" : ""}
                    </StatusBadge>
                  )}
                </span>
                {/* O card soma a BASE (o conferido do comprador manda sobre o
                    emitido); a diferença para o documento fica dita, não
                    escondida. Vem do servidor — aqui só formata. */}
                {(resumo.aReceberEmDivergencia ?? 0) > 0 && (
                  <span className="text-erro" data-testid="a-receber-divergencia">
                    {fmtMoney(resumo.aReceberEmDivergencia)} em divergência
                  </span>
                )}
              </span>
            ) : undefined
          }
        />

        <StatCard
          rotulo={
            /* A janela sobe pro RÓTULO: é a informação que impede comparar este kg com o kg
               do card ao lado, e em 10px no rodapé ninguém a lia. */
            `Colhido${escopoVariedade}${resumo?.colhido.janelaSaldo ? ` · ${fmtDataCurta(resumo.colhido.janelaSaldo.inicio)}–${fmtDataCurta(resumo.colhido.janelaSaldo.fim)}` : ""}`
          }
          valor={resumo && resumo.colhido.kgTotal > 0 ? fmtKg(resumo.colhido.kgTotal) : "—"}
          legenda={
            <div className="space-y-0.5 text-[10px] leading-snug">
              {resumo && resumo.colhido.porVariedade.length > 0 && (
                <p>
                  {resumo.colhido.porVariedade.map((v, i) => (
                    <span key={v.variedadeId}>
                      {i > 0 && " · "}
                      {v.nome} {v.kgPorCacho.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg/cacho
                    </span>
                  ))}
                </p>
              )}
              {/* A BASE do indicador, dita em voz alta. O kg grande acima é "kg vendido" (soma
                  tudo); o kg/cacho sai só do que tem rateio. Bases diferentes de propósito — e foi
                  exatamente a cobertura implícita que deixou julho mostrar 25,9 em vez de 18,37. */}
              {resumo && resumo.colhido.kgAlocadoTotal > 0 && resumo.colhido.kgAlocadoTotal < resumo.colhido.kgTotal && (
                <p data-testid="texto-cobertura-rateio">
                  base: {fmtKg(resumo.colhido.kgAlocadoTotal)} de {fmtKg(resumo.colhido.kgTotal)} vendidos
                </p>
              )}
              {resumo && resumo.colhido.vendasSemColheitaEncontrada > 0 && (
                <p>
                  <StatusBadge tom="atencao">
                    {resumo.colhido.vendasSemColheitaEncontrada} venda(s) sem colheita encontrada
                  </StatusBadge>
                </p>
              )}
              {/* O número que antes não existia: colhido que ninguém reivindicou. Sem ele, o
                  sistema é incapaz de distinguir "não vendi" de "esqueci de associar".
                  A janela é sempre escrita junto porque ela NÃO é o filtro da tela: o filtro
                  delimita a data da venda, e esta é a janela de colheita que essas vendas cobrem.
                  Distinguir "zero" de "não calculei" é metade do valor do indicador — por isso o
                  caso alocado também aparece, e só some quando não há janela nenhuma. */}
              {resumo?.colhido.janelaSaldo && (
                resumo.colhido.saldoCachos > 0 ? (
                  <p data-testid="texto-saldo-cachos">
                    <StatusBadge tom="atencao">
                      {resumo.colhido.saldoCachos.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} cachos sem venda associada
                    </StatusBadge>
                  </p>
                ) : (
                  <p data-testid="texto-saldo-zerado">
                    <StatusBadge tom="sucesso">
                      todo o colhido de {fmtDataCurta(resumo.colhido.janelaSaldo.inicio)} a {fmtDataCurta(resumo.colhido.janelaSaldo.fim)} está alocado
                    </StatusBadge>
                  </p>
                )
              )}
              {/* Sempre visível para ADMIN — desabilitado com tooltip quando não há associação
                  nenhuma no recorte, nunca um `return` mudo no clique. */}
              {isAdmin && resumo && (
                <button
                  type="button"
                  className="underline font-semibold text-foreground disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                  disabled={recalcular.isPending || !resumo.colhido.janelaRecalculo}
                  title={
                    !resumo.colhido.janelaRecalculo
                      ? "Nenhuma venda deste filtro tem colheita associada — não há período para recalcular. Associe a colheita na venda primeiro."
                      : `Recalcula ${fmtDataCurta(resumo.colhido.janelaRecalculo.inicio)}–${fmtDataCurta(resumo.colhido.janelaRecalculo.fim)}`
                  }
                  onClick={handleRecalcularRateio}
                  data-testid="button-recalcular-rateio"
                >
                  {recalcular.isPending ? "Recalculando…" : "Recalcular rateio"}
                </button>
              )}
            </div>
          }
        />

        <CardClassificacao classificacao={resumo?.classificacao} kgTotalRecorte={resumo?.colhido.kgTotal ?? 0} />
      </StatCardGrid>

      {/* Sem moldura: o "card Filtros" que a Onda 1 matou nas outras telas tinha
          sobrevivido justamente aqui, na tela que era pra ser a referência. O
          botão "Filtros · N" do celular continua sendo quem abre e fecha. */}
      <PageToolbar
        className={`${filtrosAbertos ? "flex" : "hidden"} md:flex`}
        fim={
          <PageSearch
            valor={busca}
            onChange={v => definir({ q: v })}
            placeholder="Nº ou comprador"
            testId="input-busca-vendas"
          />
        }
      >
        {/* "venda em", não "Filtros:". O seletor delimita `vendas.data`; o card Colhido mede
            `colheitas.data_colheita`. Duas grandezas com os mesmos limites — foi essa
            ambiguidade que gerou o bug do saldo. Duas palavras matam a classe inteira. */}
        <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">venda em</span>
        <Select value={periodo} onValueChange={v => definir({ periodo: v })}>
          <SelectTrigger className={TRIGGER_FILTRO + " md:w-40"}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="mes-atual">Mês atual</SelectItem>
            <SelectItem value="mes-passado">Mês passado</SelectItem>
            <SelectItem value="ultimos-30-dias">Últimos 30 dias</SelectItem>
            <SelectItem value="personalizado">Período…</SelectItem>
            <SelectItem value="tudo">Tudo</SelectItem>
          </SelectContent>
        </Select>
        {periodo === "personalizado" && (
          <>
            <Input
              type="date"
              className={DATA_FILTRO}
              value={dataInicioPersonalizado}
              onChange={e => definir({ de: e.target.value })}
              data-testid="input-periodo-inicio"
            />
            <span className="text-xs text-muted-foreground">até</span>
            <Input
              type="date"
              className={DATA_FILTRO}
              value={dataFimPersonalizado}
              onChange={e => definir({ ate: e.target.value })}
              data-testid="input-periodo-fim"
            />
          </>
        )}
        <Select value={variedadeId} onValueChange={v => definir({ variedade: v })}>
          <SelectTrigger className={TRIGGER_FILTRO + " md:w-44"} data-testid="select-variedade"><SelectValue placeholder="Variedade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas variedades</SelectItem>
            {variedades.map(v => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={compradorId} onValueChange={v => definir({ comprador: v })}>
          <SelectTrigger className={TRIGGER_FILTRO + " md:w-48"}><SelectValue placeholder="Comprador" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos compradores</SelectItem>
            {compradores.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={v => definir({ status: v })}>
          <SelectTrigger className={TRIGGER_FILTRO + " md:w-44"}><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos status</SelectItem>
            <SelectItem value="EM_ABERTO_PAGAS">Em aberto e pagas</SelectItem>
            <SelectItem value="RASCUNHO">Rascunho</SelectItem>
            <SelectItem value="EMITIDA">A receber</SelectItem>
            <SelectItem value="PAGA">Paga</SelectItem>
            <SelectItem value="CANCELADA">Cancelada</SelectItem>
            <SelectItem value="COM_DIVERGENCIA">Com divergência</SelectItem>
          </SelectContent>
        </Select>
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante={modoVisao === "tabela" ? "tabela" : "cards"} linhas={5} />
      ) : vendas.length === 0 ? (
        <EmptyState
          titulo="Nenhuma venda no período"
          descricao="Amplie o período no filtro acima, ou registre a primeira venda deste recorte."
          acao={
            <Button onClick={() => abrir("/cacho/vendas/novo")}>
              <Plus className="h-4 w-4 mr-1" />Nova venda
            </Button>
          }
          testId="empty-vendas"
        />
      ) : vendasVisiveis.length === 0 ? (
        /* Busca sem resultado ≠ período sem venda. Dizer "nenhuma venda no período"
           para quem digitou "joão silva" seria mentira sobre o dado. */
        <NadaEncontrado termo={busca} />
      ) : modoVisao === "tabela" ? (
        <VendasTabela vendas={vendasVisiveis} isAdmin={isAdmin} excluir={excluir} abrir={abrir} handleExcluirCard={handleExcluirCard} variedadeId={variedadeSelecionada} compartilhamentosPorVenda={compartilhamentosPorVenda} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
          {vendasVisiveis.map(venda => {
            const setores = setoresDaVenda(venda);
            const pmv = pesoMedioDaVenda(venda);
            const totalCachos = pmv.reduce((acc, v) => acc + v.cachos, 0);
            const podeEditar = venda.status === "RASCUNHO" || venda.status === "EMITIDA";
            const podeExcluir = isAdmin && (venda.status === "RASCUNHO" || venda.status === "CANCELADA");
            return (
              <div
                key={venda.id}
                className="rounded-xl border p-3 cursor-pointer hover:bg-muted/50 flex flex-col"
                onClick={() => abrir(`/cacho/vendas/${venda.id}`)}
                data-testid={`card-venda-${venda.id}`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="font-bold text-sm">
                    {venda.numero ? `Nº ${String(venda.numero).padStart(3, "0")}/${venda.ano}` : "Rascunho"}
                    <span className="text-muted-foreground font-normal text-xs ml-1">
                      · {new Date(venda.data + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })}
                    </span>
                  </span>
                  <div className="flex items-start gap-1 flex-wrap justify-end">
                    <ChipsDaVenda venda={venda} compartilhamento={compartilhamentosPorVenda.get(venda.id)} abrir={abrir} />
                  </div>
                </div>
                <p className="text-sm font-semibold text-primary">{venda.comprador.nome}</p>
                <p className="text-xs text-muted-foreground mb-1">{resumoItens(venda) || "Sem itens ainda"}</p>

                {setores.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mb-1">
                    Setores: <b className="text-foreground font-semibold">{setores.join(", ")}</b>
                  </p>
                )}

                {/* O aviso de peso implausível existia no payload e não era desenhado aqui:
                    a venda 031 aparecia com "46,929" em verde, sem sinal nenhum, enquanto o
                    detalhe da mesma venda gritava em âmbar. Mesma banda, mesmo tratamento.
                    E três casas decimais num kg/cacho sugerem uma precisão que o número não
                    tem — o card usa uma, a linha passa a usar uma. */}
                {pmv.length > 0 ? (
                  <div className={`inline-flex flex-wrap items-center gap-1 text-[11px] rounded-md px-2 py-1 mb-1 w-fit ${
                    pmv.some(v => v.avisoPesoImplausivel) ? "text-atencao bg-atencao-suave" : "text-sucesso bg-sucesso-suave"
                  }`}>
                    ⚖️ {pmv.map((v, i) => (
                      <span key={v.variedadeId}>
                        {i > 0 && " · "}
                        <b className="font-extrabold">
                          {pmv.length > 1 ? `${v.variedadeNome} ` : ""}
                          {v.kgPorCacho.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg/cacho
                          {v.avisoPesoImplausivel && " ⚠️"}
                        </b>
                      </span>
                    ))}
                    <span className={`text-[9px] ${pmv.some(v => v.avisoPesoImplausivel) ? "text-atencao/80" : "text-sucesso/70"}`}>
                      estimado · {totalCachos.toLocaleString("pt-BR")} cachos
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-muted rounded-md px-2 py-1 mb-1 w-fit">⚖️ —</div>
                )}

                {/* Formalizar sem abrir a venda — o backfill do C2 são dezenas de
                    vendas antigas de uma sentada. */}
                <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                  <BotaoFormalizar
                    vendaId={venda.id}
                    status={venda.status}
                    compradorNome={venda.comprador.nome}
                    telefoneCadastro={venda.comprador.telefoneWhatsapp ?? venda.comprador.telefone}
                    jaFormalizada={!!compartilhamentosPorVenda.get(venda.id)}
                  />
                </div>

                <div className="flex justify-between text-sm mt-1">
                  {/* Omitido em pesagem, não zerado: "0 cx" não é lacuna, é a AFIRMAÇÃO de
                      que a carga tem zero caixas. E "—" já significa "não sei" no app
                      (decisão do #150); aqui o caso é "não se aplica". */}
                  <span className="text-muted-foreground">
                    {cargaEmPesagem(venda.itens) ? fmtKg(venda.pesoTotalKg) : `${venda.totalCaixas.toLocaleString("pt-BR")} cx · ${fmtKg(venda.pesoTotalKg)}`}
                  </span>
                  <span className="font-bold">{fmtMoney(venda.valorTotal)}</span>
                </div>

                {(podeEditar || podeExcluir) && (
                  <div className="flex justify-end gap-1 mt-auto pt-2 border-t">
                    {podeEditar && (
                      <Button
                        variant="outline" size="icon" className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); abrir(`/cacho/vendas/${venda.id}/editar`); }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {podeExcluir && (
                      <Button
                        variant="outline" size="icon" className="h-7 w-7 text-erro border-erro/30"
                        onClick={(e) => handleExcluirCard(e, venda.id)} disabled={excluir.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Larguras da tabela de vendas, MEDIDAS no Chromium com o CSS buildado — para
// cada coluna, o maior entre a largura do cabeçalho e a do pior conteúdo:
//
//   coluna          cabeçalho   pior conteúdo   declarado   folga
//   Nº / Data           79px           102px      6,5rem      +2px
//   Comprador          103px       truncável     elástica   mín. 8rem
//   Classificação      122px       truncável        8rem      +6px  ← o cabeçalho é o piso
//   Setores             81px       truncável      5,5rem      +7px  (trunca, com title)
//   Caixas              69px            67px      4,5rem      +3px
//   Kg                  42px            97px     6,25rem      +3px
//   Kg/cacho            89px           119px     5,75rem      +3px
//   Valor               65px           115px     7,25rem      +1px
//   Status              70px           139px     8,75rem      +1px  ← 8rem cortava "Não formalizada"
//   Ações               66px           116px      7,5rem      +4px  ← 5rem não cabia os três botões
//
// Status e Ações estavam ESTREITAS DEMAIS e cortavam conteúdo; as numéricas
// estavam largas demais e pagavam a diferença em scroll. Total: 60rem de fixas
// + 8rem de mínimo para a elástica = 68rem, que é o `min-w` da tabela.
const COL_NUMERO = "6.5rem";
const COL_CLASSIFICACAO = "8rem";
const COL_SETORES = "5.5rem";
const COL_CAIXAS = "4.5rem";
const COL_KG = "6.25rem";
const COL_KG_CACHO = "5.75rem";
const COL_VALOR = "7.25rem";
const COL_STATUS = "8.75rem";
const COL_ACOES = "7.5rem";

function VendasTabela({
  vendas, isAdmin, excluir, abrir, handleExcluirCard, variedadeId, compartilhamentosPorVenda,
}: {
  vendas: VendaWithRelations[];
  isAdmin: boolean;
  excluir: ReturnType<typeof useExcluirVenda>;
  abrir: (destino: string) => void;
  handleExcluirCard: (e: React.MouseEvent, id: string) => void;
  variedadeId?: string;
  compartilhamentosPorVenda: Map<string, CompartilhamentoResumo>;
}) {
  // Totais do RECORTE: com filtro de variedade, os denormalizados da venda trariam a carga
  // inteira de volta e a linha TOTAIS contradiria os cards de novo.
  const totalCaixas = vendas.reduce((acc, v) => acc + somaItens(v, variedadeId).caixas, 0);
  const vendasPorPeso = vendas.filter(v => cargaEmPesagem(v.itens)).length;
  const totalKg = vendas.reduce((acc, v) => acc + somaItens(v, variedadeId).kg, 0);
  const totalValor = vendas.reduce((acc, v) => acc + somaItens(v, variedadeId).valor, 0);

  return (
    <div className="overflow-x-auto">
      {/* Largura por <colgroup>, nunca `width` no <th>: é o que mantém a coluna
          estável quando o conteúdo muda de tamanho entre filtros.

          ⚠️ Numa tabela `table-fixed`, a coluna elástica (`null`) recebe o que
          SOBRA das fixas. O `min-w` precisa ser MAIOR que a soma das fixas —
          senão a elástica é espremida a ZERO e o seu cabeçalho transborda por
          cima do vizinho. Era exatamente o bug: 10 fixas somavam 76rem contra um
          `min-w-[68rem]`, então "Produtos" ficava com 0px e "PRODUTOS" era
          desenhado em cima de "CLASSIFICAÇÃO".

          `min-w` = 60rem (as nove fixas) + 8rem (mínimo da elástica) = 68rem.
          Abaixo disso a tabela ROLA, que é o comportamento certo — nunca
          colapsar uma coluna. Larguras medidas no Chromium com o CSS buildado
          (cabeçalho e pior conteúdo de cada coluna), não estimadas. */}
      <DataTable
        colunas={[COL_NUMERO, null, COL_CLASSIFICACAO, COL_SETORES, COL_CAIXAS, COL_KG, COL_KG_CACHO, COL_VALOR, COL_STATUS, COL_ACOES]}
        className="min-w-[68rem]"
      >
        <DataTableHeader>
          <DataTableRow>
            <DataTableHead>Nº / Data</DataTableHead>
            <DataTableHead>Comprador</DataTableHead>
            <DataTableHead>Classificação</DataTableHead>
            <DataTableHead>Setores</DataTableHead>
            <DataTableHead num>Caixas</DataTableHead>
            <DataTableHead num>Kg</DataTableHead>
            <DataTableHead num>Kg/cacho</DataTableHead>
            <DataTableHead num>Valor</DataTableHead>
            <DataTableHead>Status</DataTableHead>
            <DataTableHead num>Ações</DataTableHead>
          </DataTableRow>
        </DataTableHeader>
        <DataTableBody>
          {vendas.map(venda => {
            const setores = setoresDaVenda(venda);
            const pmv = pesoMedioDaVenda(venda);
            const recorte = somaItens(venda, variedadeId);
            const podeEditar = venda.status === "RASCUNHO" || venda.status === "EMITIDA";
            const podeExcluir = isAdmin && (venda.status === "RASCUNHO" || venda.status === "CANCELADA");
            return (
              <DataTableRow
                key={venda.id}
                className="cursor-pointer"
                onClick={() => abrir(`/cacho/vendas/${venda.id}`)}
                data-testid={`row-venda-${venda.id}`}
              >
                <DataTableCell destaque>
                  {/* `whitespace-nowrap` no número: em 7rem a coluna quebrava "Nº"
                      do "039/2026" em algumas linhas e não em outras — "1" é mais
                      estreito que "9" numa fonte proporcional, então a 031 cabia e
                      a 039 não. Duas linhas sempre, e sempre no mesmo lugar. */}
                  <span className="block whitespace-nowrap">
                    {venda.numero ? `Nº ${String(venda.numero).padStart(3, "0")}/${venda.ano}` : "Rascunho"}
                  </span>
                  <span className="block text-muted-foreground font-normal text-[11px]">{fmtDataCurta(venda.data)}</span>
                </DataTableCell>
                {/* Comprador e produtos na MESMA célula, como no card: são o "quem"
                    e o "o quê" da mesma carga, e separá-los custava uma coluna
                    inteira numa tabela que já não cabia na tela. `min-w-0` no
                    wrapper porque `truncate` dentro de célula de tabela não
                    encolhe sem ele. */}
                <DataTableCell destaque>
                  <div className="min-w-0">
                    <span className="block truncate" title={venda.comprador.nome}>{venda.comprador.nome}</span>
                    <span
                      className="block truncate text-muted-foreground font-normal text-[11px]"
                      title={resumoItens(venda)}
                    >
                      {resumoItens(venda) || "Sem itens ainda"}
                    </span>
                  </div>
                </DataTableCell>
                <DataTableCell>
                  <CelulaClassificacao venda={venda} variedadeId={variedadeId} />
                </DataTableCell>
                <DataTableCell className="text-muted-foreground truncate" title={setores.join(", ")}>
                  {setores.length > 0 ? setores.join(", ") : "—"}
                </DataTableCell>
                <DataTableNum>
                  {/* Coluna fixa não pode sumir: a palavra explica por que não há número. */}
                  {cargaEmPesagem(venda.itens)
                    ? <span className="font-sans text-[11px] text-muted-foreground">por peso</span>
                    : recorte.caixas.toLocaleString("pt-BR")}
                </DataTableNum>
                <DataTableNum>
                  {recorte.kg.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg
                  {/* O vínculo com a carga física não pode se perder: a venda 034 mostra
                      "885 kg" com "de 1.941 kg" embaixo. */}
                  {recorte.parcial && (
                    <span className="block text-[10px] text-muted-foreground">
                      de {parseFloat(String(venda.pesoTotalKg)).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg
                    </span>
                  )}
                </DataTableNum>
                <DataTableNum>
                  {pmv.length === 0
                    ? <span className="text-muted-foreground">—</span>
                    : pmv.map(v => (
                        <span
                          key={v.variedadeId}
                          className={`font-semibold block ${v.avisoPesoImplausivel ? "text-atencao" : ""}`}
                          title={v.avisoPesoImplausivel ? "kg/cacho fora da faixa esperada — a colheita associada pode estar incompleta ou nos setores errados" : undefined}
                        >
                          {pmv.length > 1 ? <span className="font-sans text-[10px] text-muted-foreground">{v.variedadeNome} </span> : ""}
                          {v.kgPorCacho.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                          {v.avisoPesoImplausivel && " ⚠️"}
                        </span>
                      ))
                  }
                </DataTableNum>
                <DataTableNum className="font-semibold">
                  {fmtMoney(recorte.valor)}
                  {recorte.parcial && (
                    <span className="block text-[10px] text-muted-foreground font-normal">de {fmtMoney(venda.valorTotal)}</span>
                  )}
                </DataTableNum>
                <DataTableCell>
                  {/* Dois chips, não um: status da venda e estado da formalização são
                      eixos independentes ("Paga" + "Não formalizada" é estado válido). */}
                  <div className="flex flex-col items-start gap-1">
                    <ChipsDaVenda venda={venda} compartilhamento={compartilhamentosPorVenda.get(venda.id)} abrir={abrir} />
                  </div>
                </DataTableCell>
                <DataTableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {(podeEditar || podeExcluir || mostraChipFormalizacao(venda.status)) && (
                    <div className="flex justify-end gap-1">
                      <BotaoFormalizar
                        vendaId={venda.id}
                        status={venda.status}
                        compradorNome={venda.comprador.nome}
                        telefoneCadastro={venda.comprador.telefoneWhatsapp ?? venda.comprador.telefone}
                        jaFormalizada={!!compartilhamentosPorVenda.get(venda.id)}
                        compacto
                      />
                      {podeEditar && (
                        <Button
                          variant="outline" size="icon" className="h-7 w-7"
                          onClick={() => abrir(`/cacho/vendas/${venda.id}/editar`)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {podeExcluir && (
                        <Button
                          variant="outline" size="icon" className="h-7 w-7 text-destructive"
                          onClick={(e) => handleExcluirCard(e, venda.id)} disabled={excluir.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </DataTableCell>
              </DataTableRow>
            );
          })}
        </DataTableBody>
        <DataTableFooter>
          <DataTableRow className="hover:bg-transparent">
            <DataTableCell className="text-[11px] font-semibold uppercase tracking-wider" colSpan={4}>
              Totais (visualização atual)
            </DataTableCell>
            <DataTableNum className="font-semibold">
              {/* Mesma regra do #149/#150: número que exclui parte do universo declara quanto
                  excluiu, no MESMO lugar onde aparece. Sem isto, esta célula somaria caixas de
                  6 vendas e a célula ao lado somaria kg de 9, calada. */}
              {vendasPorPeso === vendas.length && vendas.length > 0
                ? <span className="font-sans font-normal text-[11px] text-muted-foreground">por peso</span>
                : (
                  <>
                    {totalCaixas.toLocaleString("pt-BR")}
                    {vendasPorPeso > 0 && (
                      // A coluna tem 4,5rem (48px de conteúdo). "em 3 de 5 vendas" com
                      // `whitespace-nowrap` transbordava por cima do kg ao lado (print do
                      // Hugo, 24/09); o texto completo cabia em cinco linhas de três
                      // letras. Aqui: "3 de 5" / "vendas", em sans, e a frase inteira
                      // no tooltip. As vendas por peso já dizem "por peso" na coluna.
                      <span
                        className="block font-sans text-[10px] text-muted-foreground font-normal leading-tight"
                        title={`Caixas de ${vendas.length - vendasPorPeso} das ${vendas.length} vendas — ${vendasPorPeso} por peso, sem contagem de caixas.`}
                        data-testid="texto-cobertura-caixas"
                      >
                        {vendas.length - vendasPorPeso} de {vendas.length} vendas
                      </span>
                    )}
                  </>
                )}
            </DataTableNum>
            <DataTableNum className="font-semibold">{totalKg.toLocaleString("pt-BR")} kg</DataTableNum>
            <DataTableNum className="text-muted-foreground">—</DataTableNum>
            <DataTableNum className="font-semibold">{fmtMoney(totalValor)}</DataTableNum>
            <DataTableCell colSpan={2} />
          </DataTableRow>
        </DataTableFooter>
      </DataTable>
    </div>
  );
}
